import { ref, computed } from "vue";
import { defineStore } from "pinia";
import {
  isNotificationSupported,
  getPermissionStatus,
  requestPermission,
  getNotificationPromptStatus,
  updateNotificationPreferences,
  recordPromptDismissed,
} from "@/services/notification.service";
import {
  initializeAblyWithPush,
  activatePush,
  deactivatePush,
} from "@/services/ably.service";
import { analytics } from "@/services/analytics.service";

export const useNotificationStore = defineStore("notification", () => {
  // State
  const isEnabled = ref(false);
  const isSupported = ref(false);
  const permissionStatus = ref("default"); // 'granted' | 'denied' | 'default'
  const shouldShowPrompt = ref(false);
  const dismissCount = ref(0);
  const lastDismissedAt = ref(null);
  const isLoading = ref(false);
  const isActivating = ref(false);
  const showOptInPopup = ref(false);
  const pushInitialized = ref(false);

  // Computed
  const canEnableNotifications = computed(() => {
    return isSupported.value && permissionStatus.value !== "denied";
  });

  const isPermissionDenied = computed(() => {
    return permissionStatus.value === "denied";
  });

  const isPermissionGranted = computed(() => {
    return permissionStatus.value === "granted";
  });

  // Actions

  /**
   * Initialize the notification store
   * Call this early in the app lifecycle
   */
  function initialize() {
    isSupported.value = isNotificationSupported();
    permissionStatus.value = getPermissionStatus();
    console.log(
      "[NotificationStore] Initialized, supported:",
      isSupported.value,
      "permission:",
      permissionStatus.value,
    );
  }

  /**
   * Check if we should show the notification prompt
   * Fetches status from backend and updates local state
   */
  async function checkPromptStatus() {
    // If already enabled locally with push initialized, don't show popup again
    // This prevents backend API from overwriting local state after user enables notifications
    if (isEnabled.value && pushInitialized.value) {
      console.log(
        "[NotificationStore] Already enabled locally, skipping backend check",
      );
      shouldShowPrompt.value = false;
      return false;
    }

    if (!isSupported.value) {
      console.log("[NotificationStore] Notifications not supported");
      shouldShowPrompt.value = false;
      return false;
    }

    // If permission is denied, don't show prompt
    if (permissionStatus.value === "denied") {
      console.log("[NotificationStore] Permission denied, not showing prompt");
      shouldShowPrompt.value = false;
      return false;
    }

    isLoading.value = true;

    try {
      const { data, error } = await getNotificationPromptStatus();

      if (error) {
        console.error(
          "[NotificationStore] Failed to get prompt status:",
          error,
        );
        shouldShowPrompt.value = false;
        return false;
      }

      isEnabled.value = data.browserNotificationsEnabled;
      shouldShowPrompt.value = data.shouldShowPrompt;
      dismissCount.value = data.dismissCount;
      lastDismissedAt.value = data.lastDismissedAt;

      console.log("[NotificationStore] Prompt status:", {
        shouldShow: shouldShowPrompt.value,
        enabled: isEnabled.value,
        dismissCount: dismissCount.value,
      });

      return shouldShowPrompt.value;
    } catch (error) {
      console.error("[NotificationStore] Error checking prompt status:", error);
      shouldShowPrompt.value = false;
      return false;
    } finally {
      isLoading.value = false;
    }
  }

  /**
   * Show the opt-in popup
   */
  function openOptInPopup() {
    showOptInPopup.value = true;
    analytics.notificationPromptShown();
  }

  /**
   * Close the opt-in popup
   */
  function closeOptInPopup() {
    showOptInPopup.value = false;
  }

  /**
   * Enable notifications
   * 1. Request browser permission
   * 2. Initialize Ably with push
   * 3. Activate push
   * 4. Update backend preference
   */
  async function enableNotifications(userUUID) {
    console.log(
      "[NotificationStore] Enabling notifications for user:",
      userUUID,
    );

    // Idempotency check: skip if already enabled
    if (
      isEnabled.value &&
      pushInitialized.value &&
      permissionStatus.value === "granted"
    ) {
      console.log("[NotificationStore] Already enabled, skipping");
      return true;
    }

    isActivating.value = true;

    try {
      // Step 1: Request browser permission
      const permission = await requestPermission();
      permissionStatus.value = permission;

      if (permission !== "granted") {
        console.log("[NotificationStore] Permission not granted:", permission);
        analytics.notificationPermissionDenied();
        return false;
      }

      analytics.notificationPermissionGranted();

      // Step 2: Initialize Ably with push support
      const client = await initializeAblyWithPush(userUUID);
      if (!client) {
        console.error("[NotificationStore] Failed to initialize Ably");
        return false;
      }
      pushInitialized.value = true;

      // Step 3: Activate push notifications and subscribe to user's channel
      const activated = await activatePush(userUUID);
      if (!activated) {
        console.error("[NotificationStore] Failed to activate push");
        return false;
      }

      // Step 4: Update backend preference
      const { error } = await updateNotificationPreferences(true);
      if (error) {
        console.error("[NotificationStore] Failed to update backend:", error);
        // Don't fail - push is already activated
      }

      isEnabled.value = true;
      shouldShowPrompt.value = false;
      closeOptInPopup();

      analytics.notificationPromptAccepted();
      console.log("[NotificationStore] Notifications enabled successfully");

      return true;
    } catch (error) {
      console.error("[NotificationStore] Error enabling notifications:", error);
      return false;
    } finally {
      isActivating.value = false;
    }
  }

  /**
   * Disable notifications.
   *
   * The server preference is saved first. It decides whether a push is sent
   * at all, and saving it first means a failed save changes nothing here:
   * the toggle stays on, push stays active, and the user can try again.
   *
   * @returns {Promise<boolean>} true once the server preference is off;
   *   false on any failure, so the caller shows an error. Still a boolean,
   *   so `if (success)` callers keep working, but a failed server save now
   *   returns false instead of being logged and reported as success.
   */
  async function disableNotifications() {
    console.log("[NotificationStore] Disabling notifications");
    isActivating.value = true;

    try {
      // Update backend preference. The service resolves an HTTP failure as
      // { error } rather than throwing.
      const { error } = await updateNotificationPreferences(false);
      if (error) {
        console.error("[NotificationStore] Failed to update backend:", error);
        return false;
      }

      // Deactivate push on this browser. With the preference off the server
      // no longer sends pushes, so a failure here is logged, not fatal.
      const deactivated = await deactivatePush();
      if (!deactivated) {
        console.warn(
          "[NotificationStore] Push deactivation failed; the server preference is already off",
        );
      }

      isEnabled.value = false;
      console.log("[NotificationStore] Notifications disabled");

      return true;
    } catch (error) {
      console.error(
        "[NotificationStore] Error disabling notifications:",
        error,
      );
      return false;
    } finally {
      isActivating.value = false;
    }
  }

  /**
   * Handle when user dismisses the opt-in popup
   * @param {boolean} dontAskAgain - If true, won't prompt again
   */
  async function dismissPrompt(dontAskAgain = false) {
    console.log(
      "[NotificationStore] Dismissing prompt, dontAskAgain:",
      dontAskAgain,
    );

    closeOptInPopup();
    shouldShowPrompt.value = false;

    // Record dismissal on backend
    const { error } = await recordPromptDismissed(dontAskAgain);
    if (error) {
      console.error("[NotificationStore] Failed to record dismissal:", error);
    }

    dismissCount.value = dontAskAgain ? 2 : dismissCount.value + 1;
    lastDismissedAt.value = new Date().toISOString();

    analytics.notificationPromptDismissed({ dismissCount: dismissCount.value });
  }

  /**
   * Initialize push for users who already have notifications enabled
   * Call this after user authentication
   */
  async function initializePushIfEnabled(userUUID) {
    if (!isEnabled.value || pushInitialized.value) {
      return;
    }

    if (permissionStatus.value !== "granted") {
      console.log(
        "[NotificationStore] Permission not granted, skipping push init",
      );
      return;
    }

    console.log("[NotificationStore] Initializing push for returning user");

    try {
      const client = await initializeAblyWithPush(userUUID);
      if (client) {
        await activatePush(userUUID);
        pushInitialized.value = true;
        console.log("[NotificationStore] Push initialized for returning user");
      }
    } catch (error) {
      console.error(
        "[NotificationStore] Failed to init push for returning user:",
        error,
      );
    }
  }

  return {
    // State
    isEnabled,
    isSupported,
    permissionStatus,
    shouldShowPrompt,
    dismissCount,
    lastDismissedAt,
    isLoading,
    isActivating,
    showOptInPopup,
    pushInitialized,

    // Computed
    canEnableNotifications,
    isPermissionDenied,
    isPermissionGranted,

    // Actions
    initialize,
    checkPromptStatus,
    openOptInPopup,
    closeOptInPopup,
    enableNotifications,
    disableNotifications,
    dismissPrompt,
    initializePushIfEnabled,
  };
});
